import { isObject, cleanString, numberOrNull, booleanValue, arrayOrEmpty, stringArray } from './helpers';
import { transformUnitAmenities, reverseUnitAmenities } from './amenities';
import { transformServices } from './services';

/* ============================================================
   BEDS
============================================================ */

const BED_TYPE_MAP = {
    'Single Bed': 'single',
    'Twin Bed': 'twin',
    'Double Bed': 'double',
    'Queen Bed': 'queen',
    'King Bed': 'king',
    'Bunk Bed': 'bunk',
    'Sofa Bed': 'sofa_bed',
    'Floor Mattress': 'floor_mattress',
    'Sleeping Bag': 'sleeping_bag',
    Other: 'other'
};

const REVERSE_BED_TYPE_MAP = Object.fromEntries(Object.entries(BED_TYPE_MAP).map(([label, value]) => [value, label]));

export function transformBeds(beds = []) {
    return arrayOrEmpty(beds)
        .map((bed) => {
            let type = cleanString(bed?.type || bed?.bedType);

            type = BED_TYPE_MAP[type] || type;

            const count = numberOrNull(bed?.count ?? bed?.quantity) || 1;

            return {
                type,
                count
            };
        })
        .filter((bed) => bed.type);
}

export function reverseBeds(beds = []) {
    return arrayOrEmpty(beds).map((bed) => ({
        type: REVERSE_BED_TYPE_MAP[bed?.type] || (bed?.type ? 'Other' : 'Double Bed'),
        count: numberOrNull(bed?.count) || 1
    }));
}

/* ============================================================
   BATHROOMS
   ------------------------------------------------------------
   The Occupancy tab collects ONE plain number (formData.
   bathrooms). The backend stores ONE object with count/type/
   hotWater. There's no UI for type/hotWater today, so those
   always round-trip as their defaults.
============================================================ */

const BATHROOM_TYPE_MAP = {
    Private: 'private',
    'Private Bathroom': 'private',
    Shared: 'shared',
    'Shared Bathroom': 'shared',
    Attached: 'attached',
    Common: 'common',
    Outdoor: 'outdoor'
};

export function transformBathrooms(bathrooms) {
    if (isObject(bathrooms)) {
        return {
            count: numberOrNull(bathrooms.count ?? bathrooms.quantity) ?? 1,

            type: BATHROOM_TYPE_MAP[bathrooms.type] || cleanString(bathrooms.type) || 'private',

            hotWater: booleanValue(bathrooms.hotWater, false)
        };
    }

    if (Array.isArray(bathrooms)) {
        const first = bathrooms[0];

        if (first) {
            return {
                count: numberOrNull(first.count ?? first.quantity) ?? 1,

                type: BATHROOM_TYPE_MAP[first.type] || cleanString(first.type) || 'private',

                hotWater: booleanValue(first.hotWater, false)
            };
        }
    }

    return {
        count: numberOrNull(bathrooms) ?? 1,
        type: 'private',
        hotWater: false
    };
}

export function reverseBathrooms(bathrooms = {}) {
    if (isObject(bathrooms)) {
        return numberOrNull(bathrooms.count) ?? 1;
    }

    return numberOrNull(bathrooms) ?? 1;
}

/* ============================================================
   TENT
   ------------------------------------------------------------
   tentSchema.type (model/accommodation/listing.js) is a String
   with an enum ("dome"/"swiss"/.../"other") and no default and
   no blank option. UnitEditor always initializes formData.tent
   with tentType: '' regardless of unit type, so leaving the
   Tent Type field untouched and submitting used to send `''`
   for units.tent.type - which fails that enum validation
   ("`` is not a valid enum value"). 'other' is a real option in
   that enum, so an unset type now falls back to it instead of
   blank.
============================================================ */

export function transformTent(tent = {}) {
    if (!isObject(tent)) {
        return undefined;
    }

    return {
        type: cleanString(tent.tentType || tent.type) || 'other',

        material: cleanString(tent.material),

        furnished: booleanValue(tent.furnished, false),

        electricity: booleanValue(tent.hasElectricity ?? tent.electricity, false),

        fan: booleanValue(tent.fan, false),

        heater: booleanValue(tent.heater, false),

        airConditioned: booleanValue(tent.airConditioned, false),

        privateDeck: booleanValue(tent.privateDeck, false),

        privateBathroom: booleanValue(
            tent.hasPrivateBathroom ?? tent.hasAttachedBathroom ?? tent.privateBathroom,
            false
        ),

        beddingProvided: booleanValue(tent.beddingProvided, true),

        mattressProvided: booleanValue(tent.mattressProvided, true),

        weatherProtection: booleanValue(tent.weatherProtection, true)
    };
}

/*
 * Saved tent -> the editor's fields (material is stored since the
 * backend tentSchema got a "material" field).
 */
export function reverseTent(tent = {}) {
    if (!isObject(tent)) {
        return {
            tentType: '',
            material: '',
            hasElectricity: false,
            hasPrivateBathroom: false,
            hasAttachedBathroom: false
        };
    }

    // the saved flags the editor doesn't show (fan, heater, ...) are kept as they are
    return {
        furnished: booleanValue(tent.furnished, false),
        fan: booleanValue(tent.fan, false),
        heater: booleanValue(tent.heater, false),
        airConditioned: booleanValue(tent.airConditioned, false),
        privateDeck: booleanValue(tent.privateDeck, false),
        beddingProvided: booleanValue(tent.beddingProvided, true),
        mattressProvided: booleanValue(tent.mattressProvided, true),
        weatherProtection: booleanValue(tent.weatherProtection, true),
        tentType: cleanString(tent.type),
        material: cleanString(tent.material),
        hasElectricity: booleanValue(tent.electricity, false),
        hasPrivateBathroom: booleanValue(tent.privateBathroom, false),
        hasAttachedBathroom: booleanValue(tent.privateBathroom, false)
    };
}

/* ============================================================
   UNIT IMAGES
============================================================ */

export function transformUnitImages(images = []) {
    return arrayOrEmpty(images)
        .map((image, index) => {
            if (typeof image === 'string') {
                return {
                    url: image,
                    type: 'image',
                    category: 'other',
                    sortOrder: index,
                    isCover: index === 0
                };
            }

            if (isObject(image)) {
                const url = image.url || image.src;

                if (!url) {
                    return null;
                }

                return {
                    _id: image._id || undefined,
                    url,
                    thumbnailUrl: image.thumbnailUrl || '',

                    type: image.type === 'video' ? 'video' : 'image',

                    category: image.category || 'other',

                    caption: image.caption || '',

                    altText: image.altText || image.alt || '',

                    sortOrder: Number.isFinite(Number(image.sortOrder)) ? Number(image.sortOrder) : index,

                    isCover: image.isCover === true || (index === 0 && !images.some((i) => i?.isCover === true))
                };
            }

            return null;
        })
        .filter(Boolean);
}

/*
 * UnitEditor's isExistingImage() treats any image object with a
 * `.url` and no `.file` as an already-uploaded photo, so handing
 * it straight back `{ url }` objects is enough for it to render
 * and behave correctly as "existing" (no re-upload attempted).
 */
export function reverseUnitImages(images = []) {
    return arrayOrEmpty(images)
        .slice()
        .sort((a, b) => (numberOrNull(a?.sortOrder) ?? 0) - (numberOrNull(b?.sortOrder) ?? 0))
        .map((image) => ({
            _id: image?._id,
            url: image?.url || '',
            thumbnailUrl: image?.thumbnailUrl || '',
            type: image?.type || 'image',
            category: image?.category || 'other',
            caption: image?.caption || '',
            altText: image?.altText || '',
            isCover: Boolean(image?.isCover),
            isExisting: true
        }))
        .filter((image) => image.url);
}

/* ============================================================
   UNIT PRICING
   ------------------------------------------------------------
   The Units/Pricing tabs collect ONE pricing object. The
   backend stores an ARRAY of rate plans (transformUnitPricing
   always produces exactly one entry today). "total" is
   display-only on the frontend and was never actually sent to
   the backend, so it's recomputed here as actual + tax rather
   than coming back blank.
   ------------------------------------------------------------
   THREE fields here didn't match the real pricingSchema
   (model/accommodation/listing.js) at all, and submitting an
   edit crashed the backend with a Mongoose CastError:

   - tax: the form collects a single flat number, but the
     schema stores `tax` as an embedded { type, value, name }
     object, not a plain Number - sending a bare number made
     Mongoose throw ("Tried to set nested object field `tax`
     to primitive value"), which is what broke every edit
     submit. Now wrapped as a fixed-amount tax object; reverse
     unwraps `.value` back to the flat number the form expects.
   - unit -> priceUnit: the schema field is named `priceUnit`
     (PRICE_UNITS enum), not `unit`; sending `unit` meant this
     was silently discarded and always fell back to the
     schema's "per_night" default.
   - higherPrice -> strikePrice: same kind of silent-drop
     mismatch - the schema calls it `strikePrice`.
   - terms: schema expects an array of strings, not one
     string - there's no UI for it today (always ''), so this
     only matters once that field gets a real input.
============================================================ */

/*
 * pricing  = the one plan the form edits
 * savedPlans = the unit's saved rate plans (edit) — everything the form
 *   doesn't show (tax type, meal plan, extra-guest prices, other plans, _id)
 *   is kept from them instead of being reset.
 */
export function transformUnitPricing(pricing = {}, savedPlans = []) {
    const [saved = {}, ...otherPlans] = arrayOrEmpty(savedPlans);

    const actualPrice = numberOrNull(pricing?.actual ?? pricing?.basePrice ?? pricing?.price);

    const strikePrice = numberOrNull(pricing?.higherPrice ?? pricing?.strikePrice);

    const taxValue = numberOrNull(pricing?.tax) || 0;

    const terms = cleanString(pricing?.terms);

    return [
        {
            ...saved,

            name: cleanString(pricing?.name) || 'Standard Rate',

            basePrice: actualPrice || 0,

            actualPrice: actualPrice || 0,

            strikePrice: strikePrice || 0,

            // new units: a fixed amount (per night, see stay pricing); saved % taxes stay %
            tax: {
                type: pricing?.taxType || saved.tax?.type || 'fixed',
                value: taxValue,
                name: saved.tax?.name || 'Tax'
            },

            discountPercent: numberOrNull(pricing?.discountPercent) || 0,

            reserveAmount: numberOrNull(pricing?.reserveAmount) || 0,

            currency: cleanString(pricing?.currency) || 'INR',

            priceUnit: cleanString(pricing?.unit) || 'per_night',

            terms: terms ? terms.split('\n').map((t) => t.trim()).filter(Boolean) : []
        },
        ...otherPlans
    ];
}

/*
 * Special prices (weekend / season / early bird …) -> the schema's shape.
 * Empty fields are left out (the schema has no required dates any more);
 * the name falls back to the type so it's never empty.
 */
const RULE_NAMES = {
    weekend: 'Weekend price',
    weekday: 'Weekday price',
    season: 'Season price',
    holiday: 'Holiday price',
    festival: 'Festival price',
    date_range: 'Special dates price',
    special_event: 'Event price',
    early_bird: 'Early bird offer',
    last_minute: 'Last minute offer'
};
export function transformPricingRules(rules = []) {
    return arrayOrEmpty(rules).map((rule) => {
        const out = {
            ...(rule._id ? { _id: rule._id } : {}),
            name: cleanString(rule.name) || RULE_NAMES[rule.type] || 'Special price',
            type: rule.type,
            adjustmentType: rule.adjustmentType === 'fixed' ? 'fixed' : 'percentage',
            adjustmentValue: numberOrNull(rule.adjustmentValue) || 0,
            minimumNights: Math.max(numberOrNull(rule.minimumNights) || 1, 1),
            active: rule.active !== false
        };
        if (rule.startDate) out.startDate = String(rule.startDate).slice(0, 10);
        if (rule.endDate) out.endDate = String(rule.endDate).slice(0, 10);
        if (Array.isArray(rule.days)) out.days = rule.days.map(Number).filter((d) => d >= 0 && d <= 6);
        if (rule.daysBefore !== '' && rule.daysBefore !== null && rule.daysBefore !== undefined) {
            out.daysBefore = Math.max(numberOrNull(rule.daysBefore) || 0, 0);
        }
        return out;
    });
}

export function reverseUnitPricing(pricing = []) {
    const plan = (Array.isArray(pricing) ? pricing[0] : pricing) || {};

    const actual = numberOrNull(plan.actualPrice ?? plan.basePrice) || 0;

    const tax = numberOrNull(plan.tax?.value) || 0;

    return {
        name: cleanString(plan.name),
        terms: Array.isArray(plan.terms) ? plan.terms.join('\n') : cleanString(plan.terms),
        actual,
        tax,
        taxType: plan.tax?.type || 'fixed',
        total: actual + tax,
        higherPrice: numberOrNull(plan.strikePrice) || 0,
        discountPercent: numberOrNull(plan.discountPercent) || 0,
        reserveAmount: numberOrNull(plan.reserveAmount) || 0,
        currency: cleanString(plan.currency) || 'INR',
        unit: cleanString(plan.priceUnit) || 'per_night'
    };
}

/* ============================================================
   UNIT - FRONTEND -> BACKEND
============================================================ */

export function transformUnit(unit = {}, index = 0) {
    const occupancy = unit.occupancy || {};

    const inventory = unit.inventory || {};

    const pricing = unit.pricing || {};

    const maxGuests =
        numberOrNull(occupancy.maxGuests) || numberOrNull(occupancy.total) || numberOrNull(unit.maximumGuests) || 1;

    const adults = numberOrNull(occupancy.adults) || 1;

    const children = numberOrNull(occupancy.children) || 0;

    const infants = numberOrNull(occupancy.infants) || 0;

    const totalOccupancy = maxGuests;

    const totalUnits = numberOrNull(inventory.totalUnits) || numberOrNull(inventory.quantity) || 1;

    const activeUnits = numberOrNull(inventory.activeUnits);

    const maintenanceUnits = numberOrNull(inventory.maintenanceUnits) || 0;

    return {
        // keep the unit's id — bookings and room counts point at it
        _id: unit._id || undefined,

        name: cleanString(unit.name),

        shortName: cleanString(unit.shortName),

        description: cleanString(unit.description),

        unitType: cleanString(unit.unitType || unit.type),

        bookingType: cleanString(unit.bookingType) || 'private_unit',

        bhkType: cleanString(unit.bhkType) || undefined,

        sharingType: cleanString(unit.sharingType) || undefined,

        occupancy: {
            adults: Math.max(adults, 1),

            children,

            infants,

            total: Math.max(totalOccupancy, 1),

            extraGuestAllowed: booleanValue(occupancy.extraGuestAllowed, false),

            baseGuests: numberOrNull(occupancy.baseGuests) || Math.min(Math.max(adults, 1), maxGuests),

            maxExtraGuests: numberOrNull(occupancy.maxExtraGuests) || 0
        },

        bedrooms: numberOrNull(unit.bedrooms) || 0,

        beds: transformBeds(unit.beds),

        bathrooms: transformBathrooms(
            isObject(unit.bathrooms)
                ? unit.bathrooms
                : { count: unit.bathrooms, type: unit.bathroomType, hotWater: unit.hotWater }
        ),

        sizeSqft: numberOrNull(unit.sizeSqft ?? unit.area ?? unit.size),

        floor: numberOrNull(unit.floor),

        view: Array.isArray(unit.view) ? stringArray(unit.view) : unit.view ? [cleanString(unit.view)] : [],

        amenities: transformUnitAmenities(unit.amenities),

        /*
         * UnitEditor's Tent Details section only shows/lets the
         * user edit `tent` when unitType is 'tent' or 'campsite'
         * (see UnitEditor.jsx's `isTent`), but formData.tent is
         * always initialized to a default object regardless of
         * unit type - so a plain 'room' unit was still sending a
         * (blank/default) tent object on submit. Only send it
         * for the unit types that actually collect it.
         */
        tent:
            unit.tent && (unit.unitType === 'tent' || unit.unitType === 'campsite')
                ? transformTent(unit.tent)
                : undefined,

        images: transformUnitImages(unit.images),

        inventory: {
            totalUnits,

            activeUnits: activeUnits !== null ? activeUnits : Math.max(totalUnits - maintenanceUnits, 0),

            maintenanceUnits,

            closed: booleanValue(inventory.closed, false)
        },

        pricing: transformUnitPricing(pricing, unit.pricingPlans),

        pricingRules: transformPricingRules(unit.pricingRules),

        services: transformServices(unit.services),

        minimumGuests: Math.max(numberOrNull(unit.minGuests ?? unit.minimumGuests) || 1, 1),

        maximumGuests: Math.max(maxGuests, 1),

        isActive: unit.active !== false && unit.isActive !== false,

        displayOrder: numberOrNull(unit.displayOrder) ?? index
    };
}

/* ============================================================
   UNIT - BACKEND -> FRONTEND
============================================================ */

export function reverseUnit(unit = {}, index = 0) {
    const occupancy = unit.occupancy || {};

    const inventory = unit.inventory || {};

    return {
        _id: unit._id,
        name: cleanString(unit.name),
        shortName: cleanString(unit.shortName),
        description: cleanString(unit.description),

        unitType: cleanString(unit.unitType) || 'room',
        bookingType: cleanString(unit.bookingType) || 'private_unit',

        bhkType: cleanString(unit.bhkType),
        sharingType: cleanString(unit.sharingType),

        occupancy: {
            maxGuests: numberOrNull(occupancy.total) || numberOrNull(unit.maximumGuests) || 2,
            adults: numberOrNull(occupancy.adults) || 2,
            children: numberOrNull(occupancy.children) || 0,
            infants: numberOrNull(occupancy.infants) || 0,
            extraGuestAllowed: booleanValue(occupancy.extraGuestAllowed, false),
            baseGuests: numberOrNull(occupancy.baseGuests),
            maxExtraGuests: numberOrNull(occupancy.maxExtraGuests) || 0
        },

        bedrooms: numberOrNull(unit.bedrooms) || 0,

        beds: reverseBeds(unit.beds),

        bathrooms: reverseBathrooms(unit.bathrooms),
        bathroomType: unit.bathrooms?.type || 'private',
        hotWater: booleanValue(unit.bathrooms?.hotWater, false),

        sizeSqft: numberOrNull(unit.sizeSqft) || 0,

        /*
         * The backend stores floor as a plain number, but the
         * Occupancy tab's "Floor" field is free text (e.g.
         * "Ground Floor"). This shows the raw number on edit
         * rather than the text originally typed - a pre-existing
         * lossy conversion from the forward transform, not new.
         */
        floor: unit.floor !== null && unit.floor !== undefined ? String(unit.floor) : '',

        view: Array.isArray(unit.view) ? unit.view[0] || '' : cleanString(unit.view),

        amenities: reverseUnitAmenities(unit.amenities),

        tent: reverseTent(unit.tent),

        inventory: {
            totalUnits: numberOrNull(inventory.totalUnits) || 1,
            activeUnits: numberOrNull(inventory.activeUnits) ?? 1,
            maintenanceUnits: numberOrNull(inventory.maintenanceUnits) || 0,
            closed: booleanValue(inventory.closed, false)
        },

        pricing: reverseUnitPricing(unit.pricing),

        // the saved rate plans, so fields the form doesn't show are kept on save
        pricingPlans: arrayOrEmpty(unit.pricing),

        pricingRules: arrayOrEmpty(unit.pricingRules),

        // no UI for per-unit services — kept as saved (not wiped on save)
        services: arrayOrEmpty(unit.services),

        minGuests: numberOrNull(unit.minimumGuests) || 1,
        maxGuests: numberOrNull(unit.maximumGuests) || 2,

        images: reverseUnitImages(unit.images),

        active: unit.isActive !== false,
        displayOrder: numberOrNull(unit.displayOrder) ?? index
    };
}
